import { useEffect, useState } from "react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/status";
import GroupDashboard from "@/components/GroupDashboard";

const COLORS = ["#0F172A", "#0284C7", "#0D9488", "#D97706", "#7C3AED", "#16A34A", "#DC2626", "#64748B", "#94A3B8"];

const Stat = ({ k, v, sub, testid }) => (
  <div className="bg-white border rounded-xl p-5"><p className="text-xs uppercase tracking-wider text-slate-500">{k}</p><p className="text-3xl font-bold mt-2 tabular-nums" data-testid={testid}>{v ?? "-"}</p>{sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}</div>
);

export default function ReportsPage() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((x) => setR(x.data)); }, []);
  if (!r) return <div className="p-8 text-slate-500">Loading...</div>;
  const pie = Object.entries(r.by_status).map(([k, v]) => ({ name: STATUS[k]?.[0] || k, value: v }));
  return (
    <div className="h-full overflow-y-auto p-4 lg:p-8" data-testid="reports-page">
      <h1 className="text-3xl font-bold tracking-tight">Operational reporting</h1>
      <p className="text-sm text-slate-600 mt-1">Organization-scoped. Completion rate = signed or completed / eligible packets (excludes {r.cohort_excludes}).</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat k="Completion rate" v={`${r.completion_rate}%`} testid="report-completion-rate" />
        <Stat k="Packets" v={r.packets} sub={`${r.forms} forms - ${r.signatures} signatures`} testid="report-packets" />
        <Stat k="Unique patients" v={r.unique_patients} testid="report-unique-patients" />
        <Stat k="Avg turnaround" v={r.avg_turnaround_hours != null ? `${r.avg_turnaround_hours}h` : "-"} testid="report-turnaround" />
        <Stat k="Export backlog" v={r.export_backlog} sub="signed, not yet filed in chart" testid="report-export-backlog" />
        <Stat k="Duplicates prevented" v={r.duplicates_prevented} sub="roster replays + suppressed packets" testid="report-duplicates" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white border rounded-xl p-5 h-80"><h2 className="font-semibold mb-2">Status breakdown</h2>
          <ResponsiveContainer width="100%" height="90%"><PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} label={(e) => e.name}>
            {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
        <div className="bg-white border rounded-xl p-5 h-80"><h2 className="font-semibold mb-2">Signed per week</h2>
          <ResponsiveContainer width="100%" height="90%"><BarChart data={r.weekly}><XAxis dataKey="week" fontSize={11} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="signed" fill="var(--brand)" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
      </div>
      <div className="bg-white border rounded-xl p-5 mt-6"><h2 className="font-semibold mb-3">Completion by provider</h2>
        {r.by_provider.map((p) => (
          <div key={p.provider} className="flex items-center gap-3 py-2 border-b last:border-0 text-sm">
            <span className="w-56 truncate">{p.provider}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded"><div className="h-2 rounded" style={{ width: `${(100 * p.completed) / p.eligible}%`, background: "var(--brand)" }} /></div>
            <span className="tabular-nums w-16 text-right">{p.completed}/{p.eligible}</span>
          </div>
        ))}
      </div>
      <GroupDashboard />
    </div>
  );
}
